<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-2">
      <label class="font-bold">Título do Post</label>
      <InputText v-model="post.title" placeholder="Ex: 5 Dicas para aprender Inglês" />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <label class="font-bold">Autor</label>
        <InputText v-model="post.author_name" />
      </div>
      <div class="flex flex-col gap-2" v-if="!props.notFillable || !props.notFillable.includes('status')">
        <label class="font-bold">Status</label>
        <Dropdown v-model="post.status" :options="statusOptions" optionLabel="label" optionValue="value" />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <label class="font-bold">Imagem de Capa (Post Photo)</label>
      <FileUpload mode="basic" accept="image/*" :maxFileSize="1000000" 
        @select="onFileSelect" chooseLabel="Selecionar Imagem" class="p-button-outlined" />
      
      <div v-if="post.post_photo" class="mt-2 relative w-full h-48 border rounded overflow-hidden bg-gray-100">
        <img :src="post.className ? post.getPhoto() : post.post_photo" class="w-full h-full object-contain" />
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex justify-between items-center">
        <label class="font-bold">Tags</label>
        <Button label="Nova Tag" icon="pi pi-plus" @click="addTag" class="p-button-text p-button-sm" />
      </div>

      <div class="flex flex-wrap gap-2 p-2 border border-dashed border-iron rounded bg-gray-50">
        <div v-for="(tag, index) in tags" :key="index" class="p-inputgroup w-auto">
          <InputText v-model="tags[index]" placeholder="Tag" class="p-inputtext-sm" style="width: 120px" />
          <Button icon="pi pi-times" class="p-button-danger p-button-outlined p-button-sm" @click="removeTag(index)" />
        </div>
        <small v-if="tags.length === 0" class="text-gray-400 p-2">Nenhuma tag adicionada.</small>
      </div>
    </div>

    <Divider />

    <div class="flex flex-col gap-2">
      <label class="font-bold">Conteúdo</label>
      <Editor v-model="post.text" editorStyle="height: 320px" />
    </div>

    <div class="flex justify-end gap-2 mt-4">
      <Button label="Cancelar" icon="pi pi-times" class="p-button-text" @click="$emit('cancel')" />
      <Button label="Salvar Postagem" icon="pi pi-check" class="bg-purple-suit-forced text-white p-1" @click="handleSave()" :loading="loading" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineProps, defineEmits, watch } from 'vue';
import Editor from 'primevue/editor';
import { Button, Divider, Dropdown, FileUpload, InputText, useToast } from 'primevue';
import BlogPostService from '@/services/BlogPostService';
import { showToast } from '@/composables/toast';
import { translate } from '@/composables/translate';
import BlogPost from '@/api/models/BlogPost';
import utils from '@/composables/utils';

const props = defineProps(['postData', 'notFillable']);
const emit = defineEmits(['save', 'cancel']);

const toast = useToast();
const post = ref({
  title: null,
  author_name: '',
  text: '',
  status: '1',
  post_photo: null,
  tags: null
});
const loading = ref(false);
const hasChangedPhoto = ref(false);
const statusOptions = [
  {label: 'Ativo', value: '1'}, 
  {label: 'Inativo', value: '0'}
];
const tags = ref([]);

onMounted(() => {
  if (props.postData) {
    post.value = new BlogPost(props.postData);
    
    try {
      tags.value = post.value.getTags();
    } catch (e) {
      tags.value = [];
    }
  }
});

watch(
  () => props.postData,
  () => {
    post.value = props.postData;
  }
);

const onFileSelect = (event) => {
  const file = event.files[0];
  const reader = new FileReader();

  reader.onloadend = () => {
    post.value.post_photo = reader.result;
    hasChangedPhoto.value = true;
  };

  reader.readAsDataURL(file);
}

const addTag = () => tags.value.push('');
const removeTag = (index) => tags.value.splice(index, 1);

async function handleSave() {
  loading.value = true;

  let blogPostData = utils.copyVariable(post.value);
  if (!hasChangedPhoto.value) {
    blogPostData.post_photo = null;
  }

  blogPostData.tags = JSON.stringify(tags.value.filter(t => t.trim() !== ''));

  const response = await BlogPostService.save(blogPostData);

  if (!response.success()) {
      showToast(toast, translate(response.getMessage()), 'error');
      return;
  }

  showToast(toast, translate('post saved'));
  emit('save');

  loading.value = false;
}

</script>